import * as formik from 'formik';
import moment, { Moment } from 'moment';
import * as fabric from 'office-ui-fabric-react';
import { DayOfWeek, DefaultButton, IDatePickerStrings, ISelectableOption, Stack } from 'office-ui-fabric-react';
import React, { FC, useEffect, useState } from 'react';
import * as Yup from 'yup';
import * as api from '../../../api/generated';
import { KendoLoading } from '../../../components';
import { ShowErrorModal } from '../../queueManagement/components/ShowErrorModal';
import NugisStatisticsGraphs from './NugisStatisticsGraphs';
import NugisStatisticsSumary from './NugisStatisticsSumary';
import NugisStatisticsTable from './NugisStatisticsTable';
import NugisStatisticsTimesMonitorGraphs from './NugisStatisticsTimesMonitorGraphs';

export interface INugisStatisticFilter {
    timezoneOffset: number;
    dateFrom: Date | null;
    timeFrom: Moment | null;
    departments: number[];
    services: number[];
    practitioners: IKeyText[];
    dateTo: Date | null;
    timeTo: Moment | null;
    queues: number[];
    locations: number[];
}

export interface IKeyText {
    key: number;
    text: string;
}

export interface IFilterDropDownOption extends ISelectableOption {
    departmentIds?: number[];
}

const styles = fabric.mergeStyleSets({
    errorColor: {
        color: fabric.getTheme().palette.redDark
    }
});

const NugisStatistics: FC = () => {
    const [error, setError] = useState<string | null>(null);
    const [timestamp, setTimestamp] = useState<string>();

    const [filter, setFilter] = useState<INugisStatisticFilter>({
        timezoneOffset: (new Date().getTimezoneOffset() / 60) * -1,
        dateFrom: new Date(new Date().setHours(0, 0, 0, 0)),
        timeFrom: null,
        departments: [],
        services: [],
        practitioners: [],
        dateTo: new Date(Date.now()),
        timeTo: null,
        queues: [],
        locations: []
    });

    const [departmentOptions, setDepartmentOptions] = useState<IFilterDropDownOption[] | null>(null);
    const [serviceOptions, setServiceOptions] = useState<IFilterDropDownOption[] | null>(null);
    const [practitionerOptions, setPractitionerOptions] = useState<IFilterDropDownOption[] | null>(null);
    const [queueOptions, setQueueOptions] = useState<IFilterDropDownOption[] | null>(null);
    const [locationOptions, setLocationOptions] = useState<IFilterDropDownOption[] | null>(null);

    const fetchData = async () => {
        try {
            const [filterOptions] = await Promise.all([api.statistic.getNugisStatisticsFilterData()]);

            if (filterOptions) {
                setDepartmentOptions(
                    filterOptions.departmentOptions.map(x => {
                        return {
                            key: x.key,
                            text: x.text ? x.text : ''
                        };
                    })
                );

                setServiceOptions(
                    filterOptions.serviceOptions.map(x => {
                        return {
                            key: x.key,
                            text: x.text ? x.text : '',
                            departmentIds: x.departmentIds
                        };
                    })
                );

                setPractitionerOptions(
                    filterOptions.practitionerOptions.map(x => {
                        return {
                            key: x.key,
                            text: x.text ? x.text : '',
                            departmentIds: x.departmentIds
                        };
                    })
                );

                setQueueOptions(
                    filterOptions.queueOptions.map(x => {
                        return {
                            key: x.key,
                            text: x.text ? x.text : '',
                            departmentIds: x.departmentIds
                        };
                    })
                );

                setLocationOptions(
                    filterOptions.locationOptions.map(x => {
                        return {
                            key: x.key,
                            text: x.text ? x.text : '',
                            departmentIds: x.departmentIds
                        };
                    })
                );
            }
        } catch (ex) {
            setError(ex.error);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const onRefresh = () => {
        setTimestamp(new Date().toString());
    };

    const onClear = (props: formik.FormikProps<any>) => {
        setFilter({
            ...filter,
            timezoneOffset: (new Date().getTimezoneOffset() / 60) * -1,
            timeFrom: null,
            departments: [],
            services: [],
            practitioners: [],
            timeTo: null,
            queues: [],
            locations: []
        });

        props.setFieldValue('timeFrom', filter.timeFrom);
        props.setFieldValue('timeTo', filter.timeTo);

        setTimestamp(new Date().toString());
    };

    const onFormatDate = (date?: Date): string => {
        return date
            ? moment(date)
                  .startOf('day')
                  .format('YYYY-MM-DD')
            : ``;
    };

    const validationSchema = Yup.object<any>().shape({
        dateFrom: Yup.date()
            .required('"Data nuo" laukas negali būti tuščias.')
            .nullable()
            .test('is-greater', '"Data nuo" turi būti mažesnė už "data iki".', value => {
                return value && filter.dateTo! ? moment(value, 'YYYY-MM-DD').isSameOrBefore(moment(filter.dateTo!, 'YYYY-MM-DD')) : true;
            })
            .test('is-max-date-range-correct', 'Didžiausias galimas dienų intervalas - 31 diena.', value => {
                return Math.floor(moment(filter.dateTo!).diff(value) / 86400000) <= 31;
            }),
        dateTo: Yup.date()
            .required('"Data iki" laukas negali būti tuščias.')
            .nullable()
            .test('is-less', '"Data iki" turi būti didesnė už "data nuo".', value => {
                return value && filter.dateFrom! ? moment(value, 'YYYY-MM-DD').isSameOrAfter(moment(filter.dateFrom!, 'YYYY-MM-DD')) : true;
            }),
        timeFrom: Yup.string()
            .nullable()
            .test('is-greater', '"Trukmė nuo" turi būti mažesnė už "trukmė iki".', value => {
                return value && filter.timeTo! ? moment(value, 'HH:mm').isSameOrBefore(moment(filter.timeTo!, 'HH:mm')) : true;
            }),
        timeTo: Yup.string()
            .nullable()
            .test('is-less', '"Trukmė iki" turi būti didesnė už "trukmė nuo".', value => {
                return value && filter.timeFrom! ? moment(value, 'HH:mm').isSameOrAfter(moment(filter.timeFrom!, 'HH:mm')) : true;
            })
    });

    const DayPickerStrings: IDatePickerStrings = {
        months: ['Sausis', 'Vasaris', 'Kovas', 'Balandis', 'Gegužė', 'Birželis', 'Liepa', 'Rugpjūtis', 'Rugsėjis', 'Spalis', 'Lapkritis', 'Gruodis'],
        shortMonths: ['Sau', 'Vas', 'Kov', 'Bal', 'Geg', 'Bir', 'Lie', 'Rgp', 'Rgs', 'Spa', 'Lap', 'Gru'],
        days: ['Sekmadienis', 'Pirmadienis', 'Antradienis', 'Trečiadienis', 'Ketvirtadienis', 'Penktadienis', 'Šeštadienis'],
        shortDays: ['S', 'Pr', 'A', 'T', 'K', 'Pn', 'Š'],
        goToToday: 'Eiti į šiandien',
        prevMonthAriaLabel: 'Eiti į praėjusį mėn.',
        nextMonthAriaLabel: 'Eiti į kitą mėn.',
        prevYearAriaLabel: 'Eiti į praėjusius metus',
        nextYearAriaLabel: 'Eiti į kitus metus'
    };

    const allOptionsAreLoaded = (): boolean => {
        return departmentOptions != null && serviceOptions != null && queueOptions != null && locationOptions != null && practitionerOptions != null;
    };

    const updateDependentDropdownOptions = (options: IFilterDropDownOption[] | null) => {
        if (options) {
            options.map(x => (x.hidden = true));
            if (filter.departments.length != 0) {
                filter.departments.forEach(depId => options.filter(x => x.departmentIds?.indexOf(depId) != -1).map(x => (x.hidden = false)));
            } else {
                options.map(x => (x.hidden = false));
            }
        }
    };

    useEffect(() => {
        setFilter({ ...filter, services: [], queues: [], locations: [], practitioners: [] });

        updateDependentDropdownOptions(serviceOptions);
        updateDependentDropdownOptions(queueOptions);
        updateDependentDropdownOptions(locationOptions);
        updateDependentDropdownOptions(practitionerOptions);
    }, [filter.departments]);

    return (
        <>
            {!error && allOptionsAreLoaded() && (
                <div className="ms-Grid" dir="ltr">
                    <div className="ms-Grid-row">
                        <div className="ms-Grid-col ms-sm12">
                            <fabric.Text variant="xxLarge">Statistika</fabric.Text>
                        </div>
                    </div>
                    <div className="ms-Grid-row">
                        <fabric.Stack tokens={{ padding: 20 }}>
                            <formik.Formik
                                validationSchema={validationSchema}
                                initialValues={{ ...filter }}
                                onSubmit={() => {}}
                                render={props => (
                                    <formik.Form>
                                        <div className="ms-Grid-row">
                                            <div className="ms-Grid-col ms-sm11">
                                                <Stack horizontal disableShrink horizontalAlign={'end'}>
                                                    <Stack>
                                                        <Stack.Item align="end">
                                                            <DefaultButton text="Ieškoti" onClick={() => onRefresh()} disabled={!props.isValid} />
                                                        </Stack.Item>
                                                    </Stack>
                                                    <Stack>
                                                        <Stack.Item>
                                                            <DefaultButton text="Išvalyti" onClick={() => onClear(props)} />
                                                        </Stack.Item>
                                                    </Stack>
                                                </Stack>
                                            </div>
                                        </div>
                                        <div className="ms-Grid-row">
                                            <div className="ms-Grid-col ms-sm2 ms-md2 ms-lg2">
                                                <formik.Field
                                                    name="dateFrom"
                                                    render={({ field, form }: formik.FieldProps<Date>) => (
                                                        <fabric.DatePicker
                                                            label="Data nuo"
                                                            allowTextInput={true}
                                                            firstDayOfWeek={DayOfWeek.Monday}
                                                            value={field.value!}
                                                            formatDate={onFormatDate}
                                                            onSelectDate={(value: any) => {
                                                                if (value) {
                                                                    form.setFieldValue('dateFrom', value);
                                                                    filter.dateFrom = value;
                                                                }
                                                            }}
                                                            onChange={(event: any) => {
                                                                if (event.target.value == '') {
                                                                    form.setFieldValue('dateFrom', null);
                                                                    filter.dateFrom = null;
                                                                }
                                                            }}
                                                            strings={DayPickerStrings}
                                                        />
                                                    )}
                                                />
                                                {props.errors.dateFrom && <div className={styles.errorColor}>{props.errors.dateFrom}</div>}
                                            </div>
                                            <div className="ms-Grid-col ms-sm2 ms-md2 ms-lg2">
                                                <formik.Field
                                                    name="timeFrom"
                                                    render={({ field, form }: formik.FieldProps<string>) => (
                                                        <fabric.TextField
                                                            label="Trukmė nuo"
                                                            {...field}
                                                            type="time"
                                                            value={field.value}
                                                            onChange={({}, value: any) => form.setFieldValue('timeFrom', value)}
                                                            errorMessage={
                                                                (props.status && props.status.timeFrom) ||
                                                                (form.errors.timeFrom && form.errors.timeFrom)
                                                            }
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className="ms-Grid-col ms-sm2 ms-md2 ms-lg2">
                                                {departmentOptions && (
                                                    <formik.Field
                                                        name="departments"
                                                        render={({ form }: formik.FieldProps<IFilterDropDownOption[]>) => (
                                                            <fabric.Dropdown
                                                                multiSelect
                                                                label="Padaliniai"
                                                                selectedKeys={filter.departments}
                                                                options={departmentOptions}
                                                                onChange={({}, item: IFilterDropDownOption | undefined) => {
                                                                    if (item) {
                                                                        setFilter({
                                                                            ...filter,
                                                                            departments: item.selected
                                                                                ? [...filter.departments, item.key as number]
                                                                                : filter.departments.filter(key => key !== item.key)
                                                                        });
                                                                    }
                                                                }}
                                                                errorMessage={
                                                                    (props.status && props.status.departments) ||
                                                                    (form.errors.departments && form.errors.departments)
                                                                }
                                                            />
                                                        )}
                                                    />
                                                )}
                                            </div>
                                            <div className="ms-Grid-col ms-sm2 ms-md2 ms-lg2">
                                                {serviceOptions && (
                                                    <formik.Field
                                                        name="services"
                                                        render={({ form }: formik.FieldProps<IFilterDropDownOption[]>) => (
                                                            <fabric.Dropdown
                                                                multiSelect
                                                                label="Paslaugos"
                                                                selectedKeys={filter.services}
                                                                options={serviceOptions}
                                                                onChange={({}, item: IFilterDropDownOption | undefined) => {
                                                                    if (item) {
                                                                        setFilter({
                                                                            ...filter,
                                                                            services: item.selected
                                                                                ? [...filter.services, item.key as number]
                                                                                : filter.services.filter(key => key !== item.key)
                                                                        });
                                                                    }
                                                                }}
                                                                errorMessage={
                                                                    (props.status && props.status.services) ||
                                                                    (form.errors.services && form.errors.services)
                                                                }
                                                            />
                                                        )}
                                                    />
                                                )}
                                            </div>
                                            <div className="ms-Grid-col ms-sm2 ms-md2 ms-lg2">
                                                {practitionerOptions && (
                                                    <formik.Field
                                                        name="practitioners"
                                                        render={({ form }: formik.FieldProps<IFilterDropDownOption[]>) => (
                                                            <fabric.Dropdown
                                                                multiSelect
                                                                label="Darbuotojai"
                                                                selectedKeys={filter.practitioners.map(x => x.key)}
                                                                options={practitionerOptions}
                                                                onChange={({}, item: IFilterDropDownOption | undefined) => {
                                                                    if (item) {
                                                                        setFilter({
                                                                            ...filter,
                                                                            practitioners: item.selected
                                                                                ? [...filter.practitioners, item as IKeyText]
                                                                                : filter.practitioners.filter(i => i.key !== item.key)
                                                                        });
                                                                    }
                                                                }}
                                                                errorMessage={
                                                                    (props.status && props.status.practitioners) ||
                                                                    (form.errors.practitioners && form.errors.practitioners)
                                                                }
                                                            />
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div className="ms-Grid-row">
                                            <div className="ms-Grid-col ms-sm2 ms-md2 ms-lg2">
                                                <formik.Field
                                                    name="dateTo"
                                                    render={({ field, form }: formik.FieldProps<Date>) => (
                                                        <fabric.DatePicker
                                                            label="Data iki"
                                                            allowTextInput={true}
                                                            firstDayOfWeek={DayOfWeek.Monday}
                                                            value={field.value!}
                                                            formatDate={onFormatDate}
                                                            onSelectDate={(value: any) => {
                                                                if (value) {
                                                                    form.setFieldValue('dateTo', value);
                                                                    filter.dateTo = value;
                                                                }
                                                            }}
                                                            onChange={(event: any) => {
                                                                if (event.target.value == '') {
                                                                    form.setFieldValue('dateTo', null);
                                                                    filter.dateTo = null;
                                                                }
                                                            }}
                                                            strings={DayPickerStrings}
                                                        />
                                                    )}
                                                />
                                                {props.errors.dateTo && <div className={styles.errorColor}>{props.errors.dateTo}</div>}
                                            </div>
                                            <div className="ms-Grid-col ms-sm2 ms-md2 ms-lg2">
                                                <formik.Field
                                                    name="timeTo"
                                                    render={({ field, form }: formik.FieldProps<string>) => (
                                                        <fabric.TextField
                                                            label="Trukmė iki"
                                                            type="time"
                                                            value={field.value}
                                                            onChange={({}, value: any) => form.setFieldValue('timeTo', value)}
                                                            errorMessage={
                                                                (props.status && props.status.timeTo) || (form.errors.timeTo && form.errors.timeTo)
                                                            }
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className="ms-Grid-col ms-sm2 ms-md2 ms-lg2">
                                                {queueOptions && (
                                                    <formik.Field
                                                        name="queues"
                                                        render={({ form }: formik.FieldProps<IFilterDropDownOption[]>) => (
                                                            <fabric.Dropdown
                                                                multiSelect
                                                                label="Eilės"
                                                                selectedKeys={filter.queues}
                                                                options={queueOptions}
                                                                onChange={({}, item: IFilterDropDownOption | undefined) => {
                                                                    if (item) {
                                                                        setFilter({
                                                                            ...filter,
                                                                            queues: item.selected
                                                                                ? [...filter.queues, item.key as number]
                                                                                : filter.queues.filter(key => key !== item.key)
                                                                        });
                                                                    }
                                                                }}
                                                                errorMessage={
                                                                    (props.status && props.status.queues) ||
                                                                    (form.errors.queues && form.errors.queues)
                                                                }
                                                            />
                                                        )}
                                                    />
                                                )}
                                            </div>
                                            <div className="ms-Grid-col ms-sm2 ms-md2 ms-lg2">
                                                {locationOptions && (
                                                    <formik.Field
                                                        name="location"
                                                        render={({ form }: formik.FieldProps<IFilterDropDownOption[]>) => (
                                                            <fabric.Dropdown
                                                                multiSelect
                                                                dropdownWidth={400}
                                                                label="Aptarnavimo vietos"
                                                                selectedKeys={filter.locations}
                                                                options={locationOptions}
                                                                onChange={({}, item: IFilterDropDownOption | undefined) => {
                                                                    if (item) {
                                                                        setFilter({
                                                                            ...filter,
                                                                            locations: item.selected
                                                                                ? [...filter.locations, item.key as number]
                                                                                : filter.locations.filter(key => key !== item.key)
                                                                        });
                                                                    }
                                                                }}
                                                                errorMessage={
                                                                    (props.status && props.status.locations) ||
                                                                    (form.errors.locations && form.errors.locations)
                                                                }
                                                            />
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        </div>

                                        <fabric.Separator />
                                        <div className="ms-Grid-row">
                                            <div className="ms-Grid-col ms-sm12">
                                                <fabric.Pivot>
                                                    <fabric.PivotItem
                                                        headerText="Duomenys"
                                                        headerButtonProps={{
                                                            disabled: !props.isValid
                                                        }}
                                                    >
                                                        <div className="ms-Grid" dir="ltr">
                                                            <div className="ms-Grid-row">
                                                                <fabric.Separator />
                                                                <NugisStatisticsSumary timestamp={timestamp} filter={filter}></NugisStatisticsSumary>
                                                                <fabric.Separator />
                                                                <NugisStatisticsTable timestamp={timestamp} filter={filter}></NugisStatisticsTable>
                                                            </div>
                                                        </div>
                                                    </fabric.PivotItem>
                                                    <fabric.PivotItem
                                                        headerText="Rodiklių stebėsena"
                                                        headerButtonProps={{
                                                            disabled: !props.isValid
                                                        }}
                                                    >
                                                        <div className="ms-Grid" dir="ltr">
                                                            <div className="ms-Grid-row">
                                                                <div className="ms-Grid-col ms-sm12">
                                                                    <fabric.Separator />
                                                                    <NugisStatisticsTimesMonitorGraphs
                                                                        timestamp={timestamp}
                                                                        filter={filter}
                                                                    ></NugisStatisticsTimesMonitorGraphs>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </fabric.PivotItem>
                                                    <fabric.PivotItem
                                                        headerText="Grafikas"
                                                        headerButtonProps={{
                                                            disabled: !props.isValid
                                                        }}
                                                    >
                                                        <div className="ms-Grid" dir="ltr">
                                                            <div className="ms-Grid-row">
                                                                <div className="ms-Grid-col ms-sm12">
                                                                    <fabric.Separator />
                                                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                                        <NugisStatisticsGraphs
                                                                            timestamp={timestamp}
                                                                            filter={filter}
                                                                        ></NugisStatisticsGraphs>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </fabric.PivotItem>
                                                    {!props.isValid && (
                                                        <fabric.PivotItem
                                                            headerText="Filtre yra klaidų"
                                                            itemIcon="AlertSolid"
                                                            headerButtonProps={{
                                                                disabled: !props.isValid
                                                            }}
                                                        ></fabric.PivotItem>
                                                    )}
                                                </fabric.Pivot>
                                            </div>
                                        </div>
                                    </formik.Form>
                                )}
                            />
                        </fabric.Stack>
                    </div>
                </div>
            )}
            {allOptionsAreLoaded() == false && <KendoLoading />}
            {error && <ShowErrorModal logout={true} error={error}></ShowErrorModal>}
        </>
    );
};

export default NugisStatistics;
